import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { components, paths } from './schema';

type S = components['schemas'];
export type Plan = S['Plan'];
export type BillingInvoice = S['BillingInvoice'];
export type BillingOverview =
  paths['/api/v1/billing']['get']['responses'][200]['content']['application/json']['data'];
export type PlanId = Plan['id'];
export type BillingCycle = 'monthly' | 'yearly';

export const billingKey = ['billing'] as const;

export function useBilling(enabled = true) {
  return useQuery({
    queryKey: billingKey,
    enabled,
    queryFn: ({ signal }) => api<{ data: BillingOverview }>('/billing', { signal }).then((r) => r.data),
    staleTime: 60_000,
  });
}

export function useInvoices(enabled = true) {
  return useQuery({
    queryKey: [...billingKey, 'invoices'],
    enabled,
    queryFn: ({ signal }) =>
      api<{ data: BillingInvoice[] }>('/billing/invoices', { signal }).then((r) => r.data),
  });
}

/** Starts Stripe Checkout and sends the browser there. */
export function useCheckout() {
  return useMutation({
    mutationFn: (input: { plan: 'starter' | 'growth'; cycle: BillingCycle }) =>
      api<{ data: { url: string } }>('/billing/checkout', { method: 'POST', body: input }).then((r) => r.data),
    onSuccess: ({ url }) => window.location.assign(url),
  });
}

/** Opens the Stripe Customer Portal (change plan, card, invoices, cancel). */
export function usePortal() {
  return useMutation({
    mutationFn: () =>
      api<{ data: { url: string } }>('/billing/portal', { method: 'POST', body: {} }).then((r) => r.data),
    onSuccess: ({ url }) => window.location.assign(url),
  });
}

/** After Stripe redirects back: apply the finished checkout right away, then refresh. */
export function useSyncCheckout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      api<void>(`/billing/checkout/${encodeURIComponent(sessionId)}/sync`, { method: 'POST', body: {} }),
    onSettled: () => qc.invalidateQueries({ queryKey: billingKey }),
  });
}
